import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup } from '@angular/forms';

// Kendo Components
import { ButtonsModule } from '@progress/kendo-angular-buttons';
import { SwitchModule } from '@progress/kendo-angular-inputs';

// Services
import { ReportingService } from 'src/app/core/services/reporting/reporting.service';

/**
 * Settings Component
 * Configuration options for reporting preferences and templates
 */
@Component({
  selector: 'app-reporting-settings',
  standalone: true,
  imports: [CommonModule, FormsModule, ReactiveFormsModule, ButtonsModule, SwitchModule],
  templateUrl: './settings.component.html',
  styleUrls: ['./settings.component.scss'],
})
export class SettingsComponent implements OnInit {
  private fb = inject(FormBuilder);
  private reportingService = inject(ReportingService);

  // Forms
  public notificationForm!: FormGroup;
  public defaultsForm!: FormGroup;
  public retentionForm!: FormGroup;

  // State
  public isLoading = signal<boolean>(false);
  public isSaving = signal<boolean>(false);
  public activeSection = signal<string>('notifications');

  // Settings sections
  public sections = [
    { id: 'notifications', label: 'Notifications', icon: '🔔' },
    { id: 'defaults', label: 'Report Defaults', icon: '⚙️' },
    { id: 'retention', label: 'Data Retention', icon: '🗄️' },
    { id: 'templates', label: 'Templates', icon: '📝' },
  ];

  ngOnInit(): void {
    this.initializeForms();
    this.loadSettings();
  }

  /**
   * Initialize forms
   */
  private initializeForms(): void {
    // Notification settings
    this.notificationForm = this.fb.group({
      emailOnCompletion: [true],
      emailOnFailure: [true],
      emailRecipients: [''],
      slackNotifications: [false],
      slackWebhook: [''],
      notifyScheduleExecution: [true],
    });

    // Default report settings
    this.defaultsForm = this.fb.group({
      includeExecutiveSummary: [true],
      includeDetailedFindings: [true],
      includeRemediation: [false],
      defaultDeliveryMethod: ['download'],
      defaultDateRange: ['last_30_days'],
      autoGenerateOnSchedule: [true],
    });

    // Data retention settings
    this.retentionForm = this.fb.group({
      retainCompletedReports: [90],
      retainFailedReports: [30],
      autoDeleteOldReports: [true],
      archiveBeforeDelete: [true],
    });
  }

  /**
   * Load current settings
   */
  private loadSettings(): void {
    this.isLoading.set(true);

    // Mock implementation - would load from backend
    setTimeout(() => {
      console.log('[Mock] Loaded settings from backend');
      this.isLoading.set(false);
    }, 500);

    // Production implementation would be:
    // this.reportingService.getSettings().subscribe(...)
  }

  /**
   * Save notification settings
   */
  public saveNotificationSettings(): void {
    if (this.notificationForm.invalid) return;

    this.isSaving.set(true);
    const settings = this.notificationForm.value;

    setTimeout(() => {
      console.log('[Mock] Saved notification settings:', settings);
      this.isSaving.set(false);
      // Show success message
    }, 1000);
  }

  /**
   * Save default settings
   */
  public saveDefaultSettings(): void {
    if (this.defaultsForm.invalid) return;

    this.isSaving.set(true);
    const settings = this.defaultsForm.value;

    setTimeout(() => {
      console.log('[Mock] Saved default settings:', settings);
      this.isSaving.set(false);
      // Show success message
    }, 1000);
  }

  /**
   * Save retention settings
   */
  public saveRetentionSettings(): void {
    if (this.retentionForm.invalid) return;

    this.isSaving.set(true);
    const settings = this.retentionForm.value;

    setTimeout(() => {
      console.log('[Mock] Saved retention settings:', settings);
      this.isSaving.set(false);
      // Show success message
    }, 1000);
  }

  /**
   * Test notification settings
   */
  public testNotifications(): void {
    console.log('[Mock] Sending test notification...');
    // Show toast message
  }

  /**
   * Reset to defaults
   */
  public resetToDefaults(): void {
    if (!confirm('Are you sure you want to reset all settings to defaults?')) {
      return;
    }

    this.initializeForms();
    console.log('[Mock] Reset settings to defaults');
  }

  /**
   * Switch active section
   */
  public selectSection(sectionId: string): void {
    this.activeSection.set(sectionId);
  }

  /**
   * Check if section is active
   */
  public isSectionActive(sectionId: string): boolean {
    return this.activeSection() === sectionId;
  }
}
